/* =======================================================================
   Diploma Framing — product page enhancements.

   Responsibilities:
     1) Toggle show/hide of the custom dimensions panel via the
        "Need a custom size?" checkbox.
     2) When custom dims are entered, compute the nearest-neighbor
        pricing tier (smallest listed size that fits the customer's
        diploma in any orientation), and switch the product's selected
        variant to the matching one so the displayed price updates.
     3) Enforce the 16" x 20" cap. Oversize input shows an error and
        disables the add-to-cart button.
     4) Writes the customer's actual desired dimensions into a hidden
        `properties[Custom Dimensions]` input so the order carries the
        real number regardless of which variant was auto-selected.
     5) Prevents submit if School / Institution is empty, or if custom
        sizing is toggled on with invalid dims.

   Self-contained: no theme.js edits. Hooks via document-level event
   delegation so it's safe across product re-renders.
   ======================================================================= */

(function () {
  'use strict';

  function init() {
    var container = document.querySelector('[data-diploma-fields]');
    if (!container) return;

    var configNode = document.querySelector('[data-diploma-config]');
    if (!configNode) return;

    var config;
    try { config = JSON.parse(configNode.textContent); }
    catch (e) { return; }

    var sectionId = config.sectionId;

    // ---- DOM refs -----------------------------------------------------
    var schoolInput      = container.querySelector('input[name="properties[School / Institution]"]');
    var toggle           = container.querySelector('[data-diploma-custom-toggle]');
    var customPanel      = container.querySelector('[data-diploma-custom-panel]');
    var customW          = container.querySelector('[data-diploma-custom-w]');
    var customH          = container.querySelector('[data-diploma-custom-h]');
    var customOut        = container.querySelector('[data-diploma-custom-out]');
    var customStatus     = container.querySelector('[data-diploma-custom-status]');

    // Variant selectors rendered by the theme's product template.
    // Single-option selector (the <select> the user sees for Diploma Size)
    // and the hidden master variant <select name="id">.
    var singleSelector = document.querySelector(
      '.single-option-selector-' + sectionId
    );
    var masterVariantSelect = document.getElementById('ProductSelect-' + sectionId);
    var addToCartBtn = document.getElementById('AddToCart-' + sectionId);

    // The .selector-wrapper is what contains the <label> + <select> for the
    // diploma size dropdown. We hide the whole wrapper when custom mode is
    // on and inject a "Custom Size" badge in its place so the customer
    // doesn't see the dropdown silently flip to one of the standard sizes.
    var selectorWrapper = singleSelector
      ? singleSelector.closest('.selector-wrapper')
      : null;

    // Build the Custom Size indicator once and leave it hidden until needed.
    var customIndicator = null;
    if (selectorWrapper) {
      customIndicator = document.createElement('div');
      customIndicator.className = 'selector-wrapper product-form__item diploma-custom-size-indicator';
      customIndicator.setAttribute('hidden', '');
      customIndicator.innerHTML =
        '<label>Diploma Size</label>' +
        '<div class="diploma-custom-size-badge">Custom Size</div>';
      selectorWrapper.parentNode.insertBefore(customIndicator, selectorWrapper.nextSibling);
    }

    // ---- Nearest-neighbor size matcher --------------------------------
    // Returns one of config.sizes, or { error: 'too-big' } if the input
    // exceeds the 16" x 20" cap. If the input fits within the cap but is
    // larger than every listed size, the largest listed size is returned
    // (same top-tier price).
    function pickSize(userW, userH) {
      if (!userW || !userH || userW <= 0 || userH <= 0) return null;
      var w = Math.max(userW, userH);
      var h = Math.min(userW, userH);

      if (w > config.capLong || h > config.capShort) {
        return { error: 'too-big' };
      }

      var fits = [];
      for (var i = 0; i < config.sizes.length; i++) {
        var s = config.sizes[i];
        var sw = Math.max(s.w, s.h);
        var sh = Math.min(s.w, s.h);
        if (sw >= w && sh >= h) fits.push(s);
      }
      if (fits.length) {
        fits.sort(function (a, b) { return (a.w * a.h) - (b.w * b.h); });
        return fits[0];
      }
      // Within cap but bigger than every listed size — charge top tier.
      return config.sizes[config.sizes.length - 1];
    }

    // ---- Variant switching --------------------------------------------
    function selectVariantByLabel(label) {
      if (!singleSelector) return;
      // Only switch if the value differs, to avoid noisy change events.
      if (singleSelector.value === label) return;
      singleSelector.value = label;
      // Fire a change event so theme.js rebuilds the hidden master select,
      // updates the displayed price, etc.
      var evt;
      try { evt = new Event('change', { bubbles: true }); }
      catch (e) { evt = document.createEvent('Event'); evt.initEvent('change', true, true); }
      singleSelector.dispatchEvent(evt);
    }

    // ---- Handlers -----------------------------------------------------
    function setCustomMode(on) {
      if (on) {
        customPanel.removeAttribute('hidden');
        if (selectorWrapper)  selectorWrapper.setAttribute('hidden', '');
        if (customIndicator) customIndicator.removeAttribute('hidden');
      } else {
        customPanel.setAttribute('hidden', '');
        customW.value = '';
        customH.value = '';
        customOut.value = '';
        customStatus.textContent = '';
        customStatus.className = 'diploma-custom-status';
        if (addToCartBtn) addToCartBtn.disabled = false;
        if (selectorWrapper)  selectorWrapper.removeAttribute('hidden');
        if (customIndicator) customIndicator.setAttribute('hidden', '');
      }
    }

    function renderCustomStatus(pick) {
      // We only surface status text on error. On success the top-of-page
      // price auto-updates via the variant switch, which is feedback enough.
      if (!customStatus) return;
      if (pick && pick.error === 'too-big') {
        customStatus.textContent =
          'Sorry — custom framing maxes out at 16" x 20". For larger pieces ' +
          'please email theframingcollective@gmail.com for a custom quote.';
        customStatus.className = 'diploma-custom-status diploma-custom-status--error';
        if (addToCartBtn) addToCartBtn.disabled = true;
        return;
      }
      customStatus.textContent = '';
      customStatus.className = 'diploma-custom-status';
      if (addToCartBtn) addToCartBtn.disabled = false;
    }

    function onCustomDimInput() {
      var w = parseFloat(customW.value);
      var h = parseFloat(customH.value);
      if (!w || !h) {
        renderCustomStatus(null);
        customOut.value = '';
        return;
      }
      var pick = pickSize(w, h);
      if (!pick || pick.error) {
        renderCustomStatus(pick);
        customOut.value = '';
        return;
      }
      customOut.value = w + '" x ' + h + '"';
      selectVariantByLabel(pick.label);
      renderCustomStatus(pick);
    }

    // ---- Wire up ------------------------------------------------------
    if (toggle) {
      toggle.addEventListener('change', function () {
        setCustomMode(toggle.checked);
        if (toggle.checked) onCustomDimInput();
      });
    }

    if (customW) customW.addEventListener('input', onCustomDimInput);
    if (customH) customH.addEventListener('input', onCustomDimInput);

    // If the customer manually picks a Size from the dropdown while custom
    // mode is active, it effectively overrides — clear the custom panel so
    // the order isn't confusing. (The variant will already have changed.)
    if (singleSelector) {
      singleSelector.addEventListener('change', function () {
        // Only react to user-initiated selections that leave our auto-picked
        // variant — we can't easily tell, so just leave the custom out value
        // in place. (The line-item property still reads as the customer's
        // entered dims, variant reflects the picked size.)
      });
    }

    // Form submission validation.
    var form = container.closest('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (schoolInput && !schoolInput.value.trim()) {
          e.preventDefault();
          schoolInput.focus();
          schoolInput.setCustomValidity('Please enter your school or institution.');
          schoolInput.reportValidity();
          setTimeout(function () { schoolInput.setCustomValidity(''); }, 0);
          return;
        }
        if (toggle && toggle.checked) {
          var w = parseFloat(customW.value);
          var h = parseFloat(customH.value);
          var pick = pickSize(w, h);
          if (!pick || pick.error) {
            e.preventDefault();
            customW.focus();
            if (customStatus && !customStatus.textContent) {
              customStatus.textContent = 'Please enter valid custom dimensions (max 16" x 20").';
              customStatus.className = 'diploma-custom-status diploma-custom-status--error';
            }
            return;
          }
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
